import {
  AssetBreakdownDimension,
  AssetCount,
  AssetInventoryType,
  AssetStatistics,
  LastSeenWindow,
  TrendPeriod
} from 'models'

export interface AssetTabConfig {
  assetType: AssetInventoryType
  label: string
  entityNoun: string
  entityNounPlural: string
  detailsTitle: string
  /* The server validates `dimension` against a fixed per-model list; anything else is a 400. */
  breakdownDimensions: AssetBreakdownDimension[]
}

export const IDENTITIES_TAB: AssetTabConfig = {
  assetType: AssetInventoryType.Identities,
  label: 'Identities',
  entityNoun: 'Identity',
  entityNounPlural: 'Identities',
  detailsTitle: 'User Information',
  breakdownDimensions: [
    { key: 'email-domain', label: 'Email Domain', column: 'EMAIL_DOMAIN' },
    { key: 'department', label: 'Department', column: 'DEPARTMENTS' },
    { key: 'location', label: 'Location', column: 'LOCATION' },
    { key: 'group', label: 'Group', column: 'GROUP_NAMES' },
    { key: 'last-login', label: 'Last Login', column: 'LAST_LOGIN' }
  ]
}

export const DEVICES_TAB: AssetTabConfig = {
  assetType: AssetInventoryType.Devices,
  label: 'Devices',
  entityNoun: 'Device',
  entityNounPlural: 'Devices',
  detailsTitle: 'Device Information',
  breakdownDimensions: [
    { key: 'os-name', label: 'OS', column: 'OS_NAME' },
    { key: 'asset-type', label: 'Asset Type', column: 'ASSET_TYPE' },
    { key: 'last-seen', label: 'Last Seen', column: 'LAST_SEEN' }
  ]
}

export const ASSET_TABS: AssetTabConfig[] = [IDENTITIES_TAB, DEVICES_TAB]

/* The server owns this window and serves it as `days`; this is only what the card prints without one. */
export const RECENTLY_ADDED_DAYS = 30

/**
 * What the cards render when the tenant has no inventory built, or the call failed: zeros rather
 * than a skeleton that never resolves, and no predicate, so there is nothing to click through to.
 */
export const EMPTY_STATISTICS: AssetStatistics = {
  available: false,
  windows: {},
  recentlyAdded: { count: 0, days: RECENTLY_ADDED_DAYS, filter: null },
  lastUpdatedAt: null
}

export const EMPTY_COUNT: AssetCount = { total: 0, changeSinceLastMonth: null }

export const LAST_SEEN_OPTIONS: LastSeenWindow[] = [
  LastSeenWindow.All,
  LastSeenWindow.SevenDays,
  LastSeenWindow.FourteenDays,
  LastSeenWindow.ThirtyDays,
  LastSeenWindow.SixtyDays,
  LastSeenWindow.NinetyDays
]

/* The window is sent as a day count or not at all, so the labels name the days. */
export const LAST_SEEN_LABELS: Record<LastSeenWindow, string> = {
  [LastSeenWindow.SevenDays]: '7 Days',
  [LastSeenWindow.FourteenDays]: '14 Days',
  [LastSeenWindow.ThirtyDays]: '30 Days',
  [LastSeenWindow.SixtyDays]: '60 Days',
  [LastSeenWindow.NinetyDays]: '90 Days',
  [LastSeenWindow.All]: 'All'
}

export const TREND_PERIOD_OPTIONS: TrendPeriod[] = [
  TrendPeriod.OneWeek,
  TrendPeriod.OneMonth,
  TrendPeriod.ThreeMonths,
  TrendPeriod.SixMonths,
  TrendPeriod.PastYear
]

export const TREND_PERIOD_LABELS: Record<TrendPeriod, string> = {
  [TrendPeriod.OneWeek]: '1W',
  [TrendPeriod.OneMonth]: '1M',
  [TrendPeriod.ThreeMonths]: '3M',
  [TrendPeriod.SixMonths]: '6M',
  [TrendPeriod.PastYear]: '1Y'
}

/**
 * Off because the design has no search on this grid. The `search`/`search_columns` plumbing behind
 * it stays wired — the API spec gives it a section and the column manifest a `searchable` flag —
 * so restoring the control is this flag and nothing else.
 */
export const SHOW_ASSET_SEARCH = false

export const DEFAULT_LAST_SEEN = LastSeenWindow.ThirtyDays
export const DEFAULT_TREND_PERIOD = TrendPeriod.PastYear
export const LAST_SEEN_STORAGE_KEY = 'onyxia.assetInventory.lastSeen'
